import { useState, useEffect, type FormEvent } from "react";
import { AuthShell, CardThemeToggle } from "./AuthShell";
import { NetIndicator } from "./NetIndicator";
import { apiUrl } from "../../utils/jwt-storage";
import { spaNavigate } from "../../utils/navigation";

/**
 * React port of ``users/templates/users/new-ui/reset_password.html``.
 *
 * Reads ``uidb64`` and ``token`` from the URL path
 * (``/auth/reset-password/:uidb64/:token``), collects a new password
 * (with confirm), and POSTs to ``/api/auth/reset-password/``.
 *
 * The email that lands the user here (see
 * ``users/password_reset.py::send_reset_email``) points at the cloud's
 * ``/reset-password/<uidb64>/<token>/`` URL. Cloud's server-side view
 * now issues a 302 to ``/auth/reset-password/<uidb64>/<token>/``
 * (React route), so the user always ends up here regardless of which
 * host the link went through.
 */
export const ResetPassword = () => {
  const [uidb64, setUidb64] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [status, setStatus] = useState<{ text: string; kind: "success" | "error" | "offline" } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Parse ``:uidb64/:token`` off the pathname. React Router doesn't
  // wire route params into this component directly (the route is
  // registered without ``useParams`` typing wired up), and parsing
  // the URL ourselves means one fewer moving part.
  useEffect(() => {
    try {
      const m = window.location.pathname.match(/^\/auth\/reset-password\/([^/]+)\/([^/]+)\/?$/);
      if (m) {
        setUidb64(decodeURIComponent(m[1]));
        setToken(decodeURIComponent(m[2]));
      } else {
        setStatus({
          text: "This reset link is malformed. Request a new one from the sign-in page.",
          kind: "error",
        });
      }
    } catch {
      setStatus({ text: "Could not read the reset link.", kind: "error" });
    }
  }, []);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setStatus(null);
    if (password !== passwordConfirm) {
      setStatus({ text: "Passwords don't match.", kind: "error" });
      return;
    }
    if (password.length < 8) {
      setStatus({ text: "Password must be at least 8 characters.", kind: "error" });
      return;
    }
    if (!uidb64 || !token) {
      setStatus({
        text: "This reset link is malformed. Request a new one from the sign-in page.",
        kind: "error",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(apiUrl("/api/auth/reset-password/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ uidb64, token, password }),
      });
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (res.status === 200 || res.status === 204) {
        setStatus({
          text: "Password updated. Taking you to sign in…",
          kind: "success",
        });
        window.setTimeout(() => spaNavigate("/auth/login", true), 1000);
        return;
      }
      if (res.status === 503 && body.code === "offline") {
        setStatus({
          text: (body.error as string) || "You're offline. Password reset needs the cloud, reconnect and try again.",
          kind: "offline",
        });
        return;
      }
      // 400 / 410 / other. Distinguish invalid-link (token expired
      // or wrong) from generic errors so the user knows what to do
      // next.
      const code = body.code as string | undefined;
      if (code === "invalid_link" || code === "invalid_token" || res.status === 410) {
        setStatus({
          text: "This reset link has expired or was already used. Request a new one from the sign-in page.",
          kind: "error",
        });
        return;
      }
      setStatus({
        text: (body.error as string) || `Reset failed (HTTP ${res.status}).`,
        kind: "error",
      });
    } catch {
      setStatus({
        text: "Network error. Try again in a moment.",
        kind: "offline",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor =
    status?.kind === "success"
      ? "var(--color-positive-content, #047857)"
      : status?.kind === "offline"
        ? "var(--color-warning-content, #92571c)"
        : "var(--color-negative-content, #b91c1c)";

  return (
    <AuthShell>
      <div className="form-wrapper">
        <div className="form-wrapper-header">
          <h2>Set a new password</h2>
          <CardThemeToggle />
          <NetIndicator />
        </div>
        <form onSubmit={onSubmit}>
          <div className="input-wrapper">
            <label htmlFor="password">New password</label>
            <input
              type="password"
              className="lsf-input-ls"
              name="password"
              id="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
          </div>
          <div className="input-wrapper">
            <label htmlFor="password_confirm">Confirm new password</label>
            <input
              type="password"
              className="lsf-input-ls"
              name="password_confirm"
              id="password_confirm"
              autoComplete="new-password"
              minLength={8}
              required
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
          </div>
          {status && (
            <p
              style={{
                color: statusColor,
                margin: "8px 0",
                fontSize: "var(--font-size-body-small)",
                lineHeight: 1.4,
              }}
            >
              {status.text}
            </p>
          )}
          <button
            type="submit"
            aria-label="Update password"
            className="login-button"
            disabled={submitting || !uidb64 || !token}
          >
            {submitting ? "Updating…" : "Update password"}
          </button>
        </form>
        <div className="text-wrapper" style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => spaNavigate("/auth/login", true)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "var(--color-primary-content, #1e40af)",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            ← Back to sign in
          </button>
        </div>
      </div>
    </AuthShell>
  );
};

export default ResetPassword;
